import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, resolveImg } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import SEO from "@/components/SEO";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, ArrowUpRight, SquaresFour, Rows } from "@phosphor-icons/react";
import ProjectOverlay from "@/components/site/ProjectOverlay";

export default function ProjectsPage() {
  const content = useContent();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [view, setView] = useState("list");
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);

  useEffect(() => {
    const params = active === "all" ? {} : { category: active };
    api.get("/projects", { params }).then(({ data }) => setProjects(data));
  }, [active]);

  const tabs = [{ name: "All", slug: "all" }, ...categories];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f2ede2" }}>
      <SEO
        title="Projects — Nat Habitat"
        description="Explore Nat Habitat's portfolio of refined residential and commercial interior design projects across Dubai."
        path="/projects"
        image={content?.seo?.og_image}
      />
      <Nav brand={content?.brand?.name} />

      {/* Editorial heading */}
      <section className="pt-36 md:pt-44 pb-8 md:pb-10">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <Reveal>
            <h1 className="font-serif font-light text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[1.02]">
              Our work,
              <br />
              curated with care.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Filter + view toggle bar */}
      <section>
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="flex items-end justify-between border-b border-foreground/15 pb-4" data-testid="category-filters">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 md:gap-x-12">
              {tabs.map((t) => (
                <FilterTab key={t.slug} label={t.name} value={t.slug} active={active} onClick={setActive} />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="view-toggle"
                onClick={() => setView((v) => (v === "list" ? "grid" : "list"))}
                className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-foreground/60 hover:text-foreground transition-colors duration-300"
              >
                {view === "list" ? (
                  <>
                    <span className="hidden sm:inline">Grid View</span>
                    <SquaresFour size={18} weight="light" />
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">List View</span>
                    <Rows size={18} weight="light" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="pt-10 md:pt-14 pb-24 md:pb-40">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <AnimatePresence mode="wait">
            {view === "list" ? (
              <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                {projects.map((p, i) => (
                  <ListRow key={p.id} p={p} index={i} onOpen={() => setOpenIndex(i)} />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="grid md:grid-cols-2 gap-x-8 gap-y-14 items-start"
              >
                {projects.map((p, i) => (
                  <GridCard key={p.id} p={p} onOpen={() => setOpenIndex(i)} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {projects.length === 0 && (
            <p className="text-center text-foreground/50 py-24" data-testid="no-projects">No projects in this category yet.</p>
          )}
        </div>
      </section>

      {openIndex !== null && (
        <ProjectOverlay
          projects={projects}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}

      <Footer content={content} />
    </div>
  );
}

function meta(p) {
  return [p.category_name, p.area, p.year].filter(Boolean);
}

function ListRow({ p, index, onOpen }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="mb-8 md:mb-10"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
        data-testid={`project-${p.slug}`}
        className="group grid md:grid-cols-[1.5fr_1fr] items-stretch cursor-pointer border border-foreground/15 overflow-hidden transition-colors duration-500 hover:border-foreground/30"
      >
        <div className="relative overflow-hidden">
          <img
            src={resolveImg(p.cover_image)}
            alt={p.title}
            className="w-full h-[300px] md:h-[430px] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
          />
        </div>

        <div className="flex flex-col justify-between px-7 md:px-14 py-8 md:py-12">
          <div>
            <p className="text-xs tracking-[0.25em] text-accent/80">{String(index + 1).padStart(2, "0")}</p>
            <h2 className="mt-6 md:mt-8 font-serif font-light text-4xl md:text-5xl leading-[1.05]">{p.title}</h2>
            {p.location && <p className="mt-3 font-serif text-xl md:text-2xl text-accent/90">{p.location}</p>}
            <p className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.2em] text-foreground/50">
              {meta(p).map((m, k) => (
                <React.Fragment key={k}>
                  {k > 0 && <span className="text-foreground/25">•</span>}
                  <span>{m}</span>
                </React.Fragment>
              ))}
            </p>
          </div>

          <span className="mt-10 md:mt-6 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-foreground group-hover:gap-5 transition-all duration-400">
            View Project
            <ArrowRight size={18} weight="light" className="transition-transform duration-400 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function GridCard({ p, onOpen }) {
  return (
    <motion.div layout initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
        data-testid={`project-${p.slug}`}
        className="group block cursor-pointer"
      >
        <div className="relative overflow-hidden">
          <img
            src={resolveImg(p.cover_image)}
            alt={p.title}
            className="w-full h-[380px] md:h-[520px] object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-105"
          />
        </div>
        <div className="mt-6 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-accent">{p.category_name}</p>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl font-light">{p.title}</h2>
            <p className="mt-1 text-sm text-foreground/60">{[p.location, p.area, p.year].filter(Boolean).join(" · ")}</p>
          </div>
          <ArrowUpRight size={22} className="mt-2 text-foreground/50 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
        </div>
      </div>
    </motion.div>
  );
}

function FilterTab({ label, value, active, onClick }) {
  const is = active === value;
  return (
    <button
      data-testid={`filter-${value}`}
      onClick={() => onClick(value)}
      className="relative pb-1 text-[11px] uppercase tracking-[0.25em] transition-colors duration-300"
      style={{ color: is ? "hsl(var(--foreground))" : "hsl(var(--foreground) / 0.5)" }}
    >
      {label}
      {is && (
        <motion.span
          layoutId="filter-underline"
          className="absolute left-0 right-0 -bottom-4 h-[2px] bg-accent"
        />
      )}
    </button>
  );
}
